import React, { useEffect, useMemo, useRef, useState } from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import BarChart from '../charts/BarChart';
import DonutChart from '../charts/DonutChart';
import DataTable from '../DataTable';
import WellAcreageMap from '../charts/WellAcreageMap';
import WellSelector from '../WellSelector';
import { formatCurrency, formatInt } from '../../utils/format';
import { apiKey } from '../../utils/wellIdentity';
import { openDrillSelection, selectionFromWellMark } from '../../utils/drilldown';
import { createDebouncedTracker, track } from '../../lib/track';
import { useDeal } from '../../deals/DealContext';
import { dealDataUrl } from '../../deals/registry';
import { isOilPrimary, shouldShowGasVolumes } from '../../utils/commodityDisplay';

const STATUS_FILTERS = ['ALL', 'PDP', 'PDSI', 'P&A'];

function pvMm(pv10M) {
  if (pv10M == null || Number.isNaN(Number(pv10M))) return null;
  const n = Number(pv10M);
  return n > 100 ? n / 1000 : n;
}

function matchesQuery(w, q) {
  if (!q) return true;
  const hay = [w.lease, w.api, w.propnum, w.county, w.status, w.wellType]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((tok) => hay.includes(tok));
}

export default function Wells({ data, mapData: mapDataProp, onNavigate }) {
  const { kpis, topProducers, negativePv, pbpCandidates, masterWells, statusCounts } = data;
  const { deal } = useDeal();
  const oil = isOilPrimary(deal);
  const showGas = shouldShowGasVolumes(deal, masterWells) && (
    !oil || (masterWells || []).some((w) => Number(w.netGasMmcf) >= 1 || Number(w.cumGasMmcf) >= 1)
  );
  const drillWell = (d) => openDrillSelection(selectionFromWellMark(d), { path: 'wells' });
  const [mapData, setMapData] = useState(mapDataProp || null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const [modeledOnly, setModeledOnly] = useState(false);
  const [selectedWellKeys, setSelectedWellKeys] = useState([]);
  const trackTableSearch = useRef(createDebouncedTracker('well_search_used', 500)).current;
  const selectedWellKeySet = useMemo(() => new Set(selectedWellKeys), [selectedWellKeys]);

  useEffect(() => {
    if (mapDataProp) {
      setMapData(mapDataProp);
      return;
    }
    let cancelled = false;
    fetch(dealDataUrl(deal, 'map.json', import.meta.env.BASE_URL), { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => {
        if (!cancelled && payload) setMapData(payload);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mapDataProp, deal]);

  const inventory = useMemo(() => {
    const byApi = new Map();
    for (const w of mapData?.wells || []) {
      byApi.set(apiKey(w.api) || w.api, w);
    }
    const engByApi = new Map();
    for (const w of topProducers || []) {
      engByApi.set(apiKey(w.api) || w.api, w);
    }
    for (const w of negativePv || []) {
      const k = apiKey(w.api) || w.api;
      if (!engByApi.has(k)) engByApi.set(k, w);
    }

    return (masterWells || []).map((w) => {
      const k = apiKey(w.api) || w.api;
      const loc = byApi.get(k) || {};
      const eng = engByApi.get(k) || {};
      const pv10M = w.pv10M ?? eng.pv10M ?? loc.pv10M ?? null;
      return {
        ...w,
        propnum: w.propnum || eng.propnum || null,
        lat: loc.lat,
        lng: loc.lng,
        pv10M,
        pv10Display: pvMm(pv10M),
        netGasMmcf: eng.netGasMmcf ?? loc.netGasResMmcf ?? null,
        rrcSymbol: loc.rrcSymbol,
        resCat: loc.resCat || eng.resCat || null,
      };
    });
  }, [masterWells, mapData, topProducers, negativePv]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return inventory
      .filter((w) => (statusFilter === 'ALL' ? true : w.status === statusFilter))
      .filter((w) => (modeledOnly ? w.modeled : true))
      .filter((w) => matchesQuery(w, q))
      .sort((a, b) => (b.pv10Display ?? -999) - (a.pv10Display ?? -999));
  }, [inventory, statusFilter, modeledOnly, query]);

  const mapPayload = useMemo(() => {
    if (!mapData) return null;
    // Keep full map layers; filter markers to current finder set when searching/filtering.
    const apis = new Set(filtered.map((w) => apiKey(w.api) || w.api).filter(Boolean));
    const narrowing = statusFilter !== 'ALL' || modeledOnly || query.trim();
    return {
      ...mapData,
      wells: narrowing
        ? (mapData.wells || []).filter((w) => apis.has(apiKey(w.api) || w.api))
        : mapData.wells,
    };
  }, [mapData, filtered, statusFilter, modeledOnly, query]);

  const statusDonut = Object.entries(statusCounts || {}).map(([label, value]) => ({
    label,
    value,
    color: label === 'PDP' ? '#10B981' : label === 'PDSI' ? '#F59E0B' : '#DC2626',
  }));

  return (
    <div className="dashboard-page wells-finder-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Wells</h2>
          <p className="page-subtitle">
            Find any well — search, filter, map, or table · {kpis.pdp} PDP · {kpis.pdsi} PDSI ·{' '}
            {kpis.pa} P&A
          </p>
          <div className="page-quote">
            <span className="page-quote-label">{data.quoteLabel}</span>
            <span>&ldquo;{data.quote}&rdquo;</span>
          </div>
        </div>
      </div>

      <section className="dashboard-section wells-finder-toolbar">
        <WellSelector wells={inventory} autoFocus placeholder="Type lease, API, county, or status…" />
        <div className="wells-finder-filters">
          <div className="map-filters">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                type="button"
                className={`map-filter-btn ${statusFilter === s ? 'active' : ''}`}
                onClick={() => {
                  setStatusFilter(s);
                  track('well_filter_changed', {
                    filter: 'status',
                    value: s,
                    modeled_only: modeledOnly,
                  });
                }}
              >
                {s === 'ALL' ? 'All status' : s}
              </button>
            ))}
            <button
              type="button"
              className={`map-filter-btn ${modeledOnly ? 'active' : ''}`}
              onClick={() => {
                setModeledOnly((v) => {
                  const next = !v;
                  track('well_filter_changed', {
                    filter: 'modeled_only',
                    value: next,
                    status: statusFilter,
                  });
                  return next;
                });
              }}
            >
              Modeled only
            </button>
          </div>
          <label className="wells-table-search">
            <span>Filter table / map</span>
            <input
              type="search"
              value={query}
              onChange={(e) => {
                const next = e.target.value;
                setQuery(next);
                const q = next.trim();
                if (q.length >= 2) {
                  trackTableSearch({
                    action: 'query',
                    query_len: q.length,
                    source: 'wells_table',
                    status: statusFilter,
                    modeled_only: modeledOnly,
                  });
                }
              }}
              placeholder="Narrow the list…"
            />
          </label>
        </div>
        <p className="chart-subtitle">
          Showing <strong>{filtered.length}</strong> of {inventory.length} wells
          {query.trim() ? ` matching “${query.trim()}”` : ''}
          {' · '}
          click map, chart, or row to open detail
        </p>
      </section>

      <KPIGrid>
        <KPICard label="PDP wells" value={formatInt(kpis.pdp)} sub="In the seller forecast case" color="var(--green)" />
        <KPICard label="PDSI" value={formatInt(kpis.pdsi)} sub="Shut-in · not modeled" color="var(--amber)" />
        <KPICard label="P&A" value={formatInt(kpis.pa)} sub="Incl. 7.16 reclass" color="var(--red)" />
        <KPICard
          label="Negative-PV10 PDPs"
          value={formatInt(negativePv.length)}
          sub="Still in package PV"
          color="var(--navy)"
        />
      </KPIGrid>

      <section className="dashboard-section">
        <div className="chart-card map-card">
          <div className="map-toolbar">
            <div>
              <div className="chart-title">Well locations</div>
              <div className="chart-subtitle">
                Marker size ≈ PV10 · click to select · Shift/⌘-click multi-select · open detail
                from popup or toolbar
              </div>
            </div>
            {onNavigate && (
              <button type="button" className="btn-secondary" onClick={() => onNavigate('map')}>
                Full map page →
              </button>
            )}
          </div>
          {mapPayload ? (
            <WellAcreageMap
              data={mapPayload}
              statusFilter="ALL"
              onWellSelect={drillWell}
              selectedWellKeys={selectedWellKeys}
              onSelectionChange={setSelectedWellKeys}
              resolveWells={mapData?.wells}
            />
          ) : (
            <p className="engine-note">Loading map layers…</p>
          )}
          <div className="map-legend">
            <span className="map-legend-item">
              <i style={{ background: '#10B981' }} /> PDP
            </span>
            <span className="map-legend-item">
              <i style={{ background: '#E8A317' }} /> PDSI
            </span>
            <span className="map-legend-item">
              <i style={{ background: '#94A3B8' }} /> P&A
            </span>
            <span className="map-legend-note">Larger markers = higher PV10 · Boswell outlined</span>
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">Status mix</div>
            <div className="chart-subtitle">Master list inventory</div>
            <DonutChart
              data={statusDonut}
              centerLabel="Wells"
              centerValue={String(masterWells.length)}
            />
          </div>
          <div className="chart-card">
            <div className="chart-title">Top PDP by PV10</div>
            <div className="chart-subtitle">$MM · click a bar to open detail</div>
            <BarChart
              data={topProducers.map((w) => ({ ...w, pv10MM: w.pv10M / 1000 }))}
              xKey="lease"
              yKey="pv10MM"
              horizontal
              yFormat={(v) => formatCurrency(v * 1e6)}
              color={(d) => (d.lease.includes('BOSWELL') ? '#F59E0B' : '#1B355C')}
              barLabel
              onBarClick={drillWell}
            />
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-title">Well inventory</div>
        <p className="chart-subtitle" style={{ marginBottom: 12 }}>
          First prod / cum are historical gross production — not EUR and not the
          net reserves column. Empty cells mean no product history (PDSI / unmodeled /
          behind-pipe).
        </p>
        <DataTable
          columns={[
            { key: 'lease', label: 'Lease' },
            { key: 'status', label: 'Status' },
            { key: 'wellType', label: 'Type' },
            { key: 'county', label: 'County' },
            {
              key: 'pv10Display',
              label: 'PV10 $MM',
              format: (v) => (v == null ? '—' : v.toFixed(2)),
            },
            ...(oil
              ? [{
                key: 'netOilMbbl',
                label: 'Net Mbbl',
                format: (v) => (v == null ? '—' : formatInt(v)),
              }]
              : []),
            ...(showGas
              ? [{
                key: 'netGasMmcf',
                label: 'Net MMcf',
                format: (v) => (v == null ? '—' : formatInt(v)),
              }]
              : []),
            {
              key: 'firstProdDate',
              label: 'First prod',
              format: (v, row) => {
                if (v) return String(v).slice(0, 10);
                if (row.status === 'PDSI' || row.status === 'P&A') return '—';
                if (!row.modeled) return '—';
                return 'No history';
              },
            },
            ...(showGas
              ? [{
                key: 'cumGasMmcf',
                label: 'Cum gas MMcf',
                format: (v, row) => {
                  if (v != null) return formatInt(v);
                  if (row.hasProdHistory === false || row.status === 'PDSI' || !row.modeled) {
                    return '—';
                  }
                  return '—';
                },
              }]
              : []),
            { key: 'wi', label: 'WI %', format: (v) => (v == null ? '—' : Number(v).toFixed(1)) },
            { key: 'nri', label: 'NRI %', format: (v) => (v == null ? '—' : Number(v).toFixed(1)) },
            { key: 'api', label: 'API' },
            { key: 'modeled', label: 'Modeled', format: (v) => (v ? 'Yes' : 'No') },
          ]}
          data={filtered}
          defaultSort="pv10Display"
          maxHeight={480}
          onRowClick={drillWell}
          rowClassName={(row) => {
            const k = apiKey(row.api) || row.api;
            return k && selectedWellKeySet.has(k) ? 'dtable-row-selected' : '';
          }}
        />
      </section>

      {negativePv.length > 0 && (
        <section className="dashboard-section">
          <div className="section-title">Negative PV10 PDPs</div>
          <p className="chart-subtitle" style={{ marginBottom: 12 }}>
            Included at face value in package PV (not floored). Click for shut-in / P&A review.
          </p>
          <DataTable
            columns={[
              { key: 'lease', label: 'Lease' },
              { key: 'nri', label: 'NRI %', format: (v) => v.toFixed(1) },
              { key: 'netGasMmcf', label: 'Net MMcf', format: (v) => v.toFixed(2) },
              { key: 'pv10M', label: 'PV10 ($M)', format: (v) => v.toFixed(2) },
            ]}
            data={negativePv}
            defaultSort="pv10M"
            defaultDir="asc"
            onRowClick={drillWell}
          />
        </section>
      )}

      <section className="dashboard-section">
        <div className="section-title">Behind-pipe candidates</div>
        <DataTable
          columns={[
            { key: 'well', label: 'Well' },
            { key: 'zones', label: 'Zones' },
            { key: 'netMmcf', label: 'Net MMcf', format: formatInt },
            { key: 'pv10M', label: 'PV10 ($M)', format: (v) => `$${v.toFixed(0)}` },
            { key: 'capexK', label: 'Capex ($K)', format: (v) => `$${v}` },
          ]}
          data={pbpCandidates}
          onRowClick={(row) => drillWell({ lease: row.well, api: row.api, propnum: row.propnum })}
        />
      </section>
    </div>
  );
}
